/**
 * RememberPage — Onthouden (spec §8.2).
 *
 * Search, the five filter chips (Alles, Vastgezet, Urgent, Gedaan,
 * Gearchiveerd), the six canonical categories, create/edit in a BottomSheet
 * with a visible SaveBar, pin/urgent/done/archive/restore/delete with
 * accessible labelled controls, a leave-with-unsaved-changes guard (in the
 * sheet), device-only drafts ('rust-remember-draft', labelled) with
 * retry-on-reconnect, and honest SyncStatus on every write. Notes are sorted
 * pinned-first, then updatedAt descending; empty states are filter-specific.
 */
import { useState } from 'react'
import {
  Banner,
  Button,
  Card,
  Chip,
  ConfirmDestructiveAction,
  EmptyState,
  ErrorState,
  IconButton,
  Skeleton,
  StatusChip,
  SyncStatus,
  TextField,
  useToast,
} from '@/components/ui'
import { PlusIcon, TrashIcon } from '@/components/icons'
import { PageHeader } from '@/components/navigation'
import type { Note } from '@/types'
import { NOTE_FILTERS, visibleNotes, type NoteFilterValue } from './categories'
import { draftKeyFor, NEW_NOTE_DRAFT_KEY, type NoteDraft } from './drafts'
import { NoteCard } from './NoteCard'
import { NoteFormSheet, type NoteFormTarget } from './NoteFormSheet'
import { useNotes } from './useNotes'
import './reminders.css'

type FormSession = { target: NoteFormTarget; key: number }

const EMPTY_STATE_COPY: Record<NoteFilterValue, { title: string; body: string }> = {
  all: {
    title: 'Nog geen notities',
    body: 'Maak je eerste notitie om niets meer te vergeten.',
  },
  pinned: {
    title: 'Geen vastgezette notities',
    body: 'Zet een notitie vast om hem altijd bovenaan te zien.',
  },
  urgent: {
    title: 'Geen urgente notities',
    body: 'Markeer een notitie als urgent als die extra aandacht nodig heeft.',
  },
  done: {
    title: 'Geen afgeronde notities',
    body: 'Notities die je als gedaan markeert verschijnen hier.',
  },
  archived: {
    title: 'Geen gearchiveerde notities',
    body: 'Gearchiveerde notities worden hier bewaard tot je ze terugzet.',
  },
}

export default function RememberPage() {
  const {
    notes,
    status,
    error,
    syncStates,
    drafts,
    online,
    pendingWrites,
    refresh,
    saveNote,
    updateFlags,
    deleteNote,
    persistDraft,
    discardDraft,
    retryDraft,
  } = useNotes()
  const { showToast } = useToast()

  const [query, setQuery] = useState('')
  const [filter, setFilter] = useState<NoteFilterValue>('all')
  const [formSession, setFormSession] = useState<FormSession | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<Note | null>(null)
  const [deleting, setDeleting] = useState(false)
  const [retryingDraftKey, setRetryingDraftKey] = useState<string | null>(null)

  const visible = visibleNotes(notes, filter, query)
  const filtersActive = query.trim().length > 0 || filter !== 'all'

  const draftEntries = Object.entries(drafts).filter(([key]) => {
    if (!formSession) return true
    const openKey =
      formSession.target.kind === 'edit'
        ? draftKeyFor(formSession.target.note.id)
        : formSession.target.kind === 'draft'
          ? draftKeyFor(formSession.target.draft.noteId)
          : draftKeyFor(null)
    return key !== openKey
  })

  const openNewForm = () => {
    // Resume an unsaved 'new' draft instead of starting a second one.
    const newDraft = drafts[NEW_NOTE_DRAFT_KEY]
    setFormSession(
      newDraft
        ? { target: { kind: 'draft', draft: newDraft }, key: Date.now() }
        : { target: { kind: 'new' }, key: Date.now() },
    )
  }

  const openEditForm = (note: Note) => {
    setFormSession({ target: { kind: 'edit', note }, key: Date.now() })
  }

  const openDraftForm = (draft: NoteDraft) => {
    setFormSession({ target: { kind: 'draft', draft }, key: Date.now() })
  }

  const closeForm = () => setFormSession(null)

  const handleSaved = () => {
    closeForm()
    showToast({ message: 'Notitie opgeslagen in de cloud.', tone: 'success' })
  }

  const handleCancelForm = () => {
    // Explicit discard: remove the device-only draft for this form session.
    if (formSession) {
      const noteId =
        formSession.target.kind === 'edit'
          ? formSession.target.note.id
          : formSession.target.kind === 'draft'
            ? formSession.target.draft.noteId
            : null
      discardDraft(draftKeyFor(noteId))
    }
    closeForm()
  }

  const handleToggle = async (note: Note, patch: Partial<Pick<Note, 'pinned' | 'urgent' | 'done' | 'archived'>>, doneMessage: string) => {
    const ok = await updateFlags(note, patch)
    showToast(
      ok
        ? { message: doneMessage, tone: 'success' }
        : { message: 'Bijwerken is mislukt. Probeer het opnieuw.', tone: 'error' },
    )
  }

  const handleConfirmDelete = async () => {
    if (!deleteTarget || deleting) return
    setDeleting(true)
    const ok = await deleteNote(deleteTarget.id)
    setDeleting(false)
    if (ok) {
      showToast({ message: 'Notitie verwijderd.', tone: 'success' })
      setDeleteTarget(null)
    } else {
      showToast({ message: 'Verwijderen is mislukt. Probeer het opnieuw.', tone: 'error' })
    }
  }

  const handleRetryDraft = async (key: string) => {
    if (retryingDraftKey) return
    setRetryingDraftKey(key)
    const ok = await retryDraft(key)
    setRetryingDraftKey(null)
    showToast(
      ok
        ? { message: 'Concept gesynchroniseerd met de cloud.', tone: 'success' }
        : { message: 'Synchronisatie mislukt. Het concept blijft op dit apparaat bewaard.', tone: 'error' },
    )
  }

  const cloudUnavailable = status === 'error' && error?.code === 'SUPABASE_NOT_CONFIGURED'
  const emptyCopy = EMPTY_STATE_COPY[filter]

  return (
    <div className="reminders-page">
      <PageHeader title="Onthouden" subtitle="Alles wat je niet wilt vergeten." />

      {!online ? (
        <Banner tone="warning">
          Je bent offline. Wijzigingen worden op dit apparaat bewaard en gesynchroniseerd zodra je
          weer online bent.
        </Banner>
      ) : null}
      {pendingWrites > 0 ? (
        <Banner tone="info">
          {pendingWrites === 1
            ? '1 wijziging wacht op synchronisatie.'
            : `${pendingWrites} wijzigingen wachten op synchronisatie.`}
        </Banner>
      ) : null}

      {status === 'error' && error ? (
        <ErrorState
          title={cloudUnavailable ? 'Cloudverbinding niet ingesteld' : 'Notities laden mislukt'}
          error={error}
          onRetry={error.retryable ? () => void refresh() : undefined}
        />
      ) : null}
      {cloudUnavailable ? (
        <Banner tone="info">
          Je kunt notities opslaan op dit apparaat. Ze worden gesynchroniseerd zodra de
          cloudverbinding beschikbaar is.
        </Banner>
      ) : null}

      <div className="reminders-search">
        <TextField
          label="Zoeken"
          value={query}
          onChange={setQuery}
          placeholder="Zoek op titel, inhoud of categorie"
        />
      </div>

      <div className="reminders-chips" role="group" aria-label="Filter notities">
        {NOTE_FILTERS.map((option) => (
          <Chip
            key={option.value}
            selected={option.value === filter}
            onClick={() => setFilter(option.value)}
          >
            {option.label}
          </Chip>
        ))}
      </div>

      <div className="reminders-add">
        <Button variant="primary" fullWidth onClick={openNewForm}>
          <PlusIcon size={18} />
          Nieuwe notitie
        </Button>
      </div>

      {status === 'loading' ? <Skeleton lines={4} /> : null}

      {draftEntries.length > 0 ? (
        <section className="reminders-drafts" aria-label="Concepten op dit apparaat">
          <h2 className="reminders-section-title">Concepten op dit apparaat</h2>
          {draftEntries.map(([key, draft]) => (
            <Card key={key} padding="sm" className="reminders-draft-card">
              <div className="reminders-draft-info">
                <div className="reminders-draft-heading">
                  <strong>{draft.title || 'Concept zonder titel'}</strong>
                  <StatusChip status="neutral">Alleen op dit apparaat</StatusChip>
                </div>
                <p className="reminders-draft-meta">{draft.category}</p>
                <SyncStatus state={draft.state} />
              </div>
              <div className="reminders-draft-actions">
                <Button variant="secondary" size="sm" onClick={() => openDraftForm(draft)}>
                  Verder bewerken
                </Button>
                {online && (draft.state === 'sync_failed' || draft.state === 'local_saved') ? (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => void handleRetryDraft(key)}
                    disabled={retryingDraftKey === key}
                    loading={retryingDraftKey === key}
                  >
                    Nu synchroniseren
                  </Button>
                ) : null}
                <IconButton
                  aria-label={`Verwijder concept ${draft.title || 'zonder titel'}`}
                  variant="danger"
                  onClick={() => discardDraft(key)}
                >
                  <TrashIcon />
                </IconButton>
              </div>
            </Card>
          ))}
        </section>
      ) : null}

      {status === 'ready' && visible.length === 0 ? (
        <EmptyState
          title={filtersActive && query.trim().length > 0 ? 'Geen notities gevonden' : emptyCopy.title}
          body={query.trim().length > 0 ? 'Pas je zoekopdracht of filter aan.' : emptyCopy.body}
          action={
            filter === 'all' && query.trim().length === 0 ? (
              <Button variant="primary" onClick={openNewForm}>
                <PlusIcon size={18} />
                Nieuwe notitie
              </Button>
            ) : undefined
          }
        />
      ) : null}

      {visible.length > 0 ? (
        <div className="reminders-list">
          {visible.map((note) => (
            <NoteCard
              key={note.id}
              note={note}
              syncState={syncStates[note.id] ?? 'cloud_saved'}
              onEdit={openEditForm}
              onTogglePin={(target) =>
                void handleToggle(target, { pinned: !target.pinned }, target.pinned ? 'Notitie losgemaakt.' : 'Notitie vastgezet.')
              }
              onToggleUrgent={(target) =>
                void handleToggle(target, { urgent: !target.urgent }, target.urgent ? 'Notitie niet meer urgent.' : 'Notitie gemarkeerd als urgent.')
              }
              onToggleDone={(target) =>
                void handleToggle(target, { done: !target.done }, target.done ? 'Notitie heropend.' : 'Notitie afgerond.')
              }
              onToggleArchive={(target) =>
                void handleToggle(target, { archived: !target.archived }, target.archived ? 'Notitie teruggezet.' : 'Notitie gearchiveerd.')
              }
              onDelete={setDeleteTarget}
            />
          ))}
        </div>
      ) : null}

      {formSession ? (
        <NoteFormSheet
          key={formSession.key}
          open
          target={formSession.target}
          onSave={saveNote}
          onSaved={handleSaved}
          onCancel={handleCancelForm}
          onPersistDraft={persistDraft}
        />
      ) : null}

      <ConfirmDestructiveAction
        open={deleteTarget !== null}
        title="Notitie verwijderen"
        body={
          deleteTarget
            ? `Weet je zeker dat je “${deleteTarget.title}” wilt verwijderen? Dit kan niet ongedaan worden.`
            : undefined
        }
        confirmLabel={deleting ? 'Verwijderen…' : 'Verwijderen'}
        onConfirm={() => void handleConfirmDelete()}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  )
}
